import { useEffect, useState } from "react";
import { Container } from "react-grid-system";
import { useMediaQuery } from "react-responsive";

import {
  Button,
  Card,
  Link as DSLink,
  Heading,
  List,
  Span,
  Spinner,
} from "@icg360/design-system";
import type { SSData } from "@keystone-ui/types";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { screenSizes } from "consts";
import { useKeystone, useUser } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import {
  marketingEligible,
  trackEvent,
  useHideFeedbackTab,
  useHideSupportChat,
} from "utils";

import styles from "./marketing-opt-in-page.module.scss";

export const MarketingOptInLandingPage = () => {
  const keystone = useKeystone();
  const user = useUser();
  useHideFeedbackTab();
  useHideSupportChat();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const finalDestination = "/my/overview";
  const data = window.SS_DATA as SSData;
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });

  const { isMarketingEligible } = marketingEligible({ data });

  useEffect(() => {
    if (!isMarketingEligible) {
      window.location.href = finalDestination;
    }
  }, [isMarketingEligible]);

  useEffect(() => {
    if (showConfirmation) {
      setTimeout(() => {
        window.location.replace(finalDestination);
      }, 2000);
    }
  }, [showConfirmation, finalDestination]);

  const updateMarketingPreferences = async (
    preferenceValue: "YES" | "NO",
    useNotifyEndpoint = false,
  ) => {
    if (user?.userId && user?.email) {
      const payload = {
        preferenceName: "marketing-optin",
        preferenceValue,
      };
      const userInfo = {
        userId: user.userId,
        email: user.email,
        firstName: user.firstName,
      };
      const response = useNotifyEndpoint
        ? await keystone.updateAccountPreferencesAndNotify(payload, userInfo)
        : await keystone.updateAccountPreferences(payload, userInfo);
      if (isError(response)) {
        trackEvent("Marketing opt-in: account preferences error");
      }
    }
  };

  const Disclaimer = () => {
    return (
      <Span
        size={isMobile ? "xs" : "sm"}
        color="quiet"
        className={styles.notificationDisclaimer}
      >
        By subscribing, you agree to receive information about SageSure&apos;s
        products and services. You can unsubscribe anytime. We take your privacy
        seriously and will never sell your information. See our{" "}
        <DSLink
          target="_blank"
          href="/privacy-policy"
          size={isMobile ? "xs" : "sm"}
        >
          privacy policy
        </DSLink>{" "}
        to learn more.
      </Span>
    );
  };

  const signUp = async () => {
    setShowConfirmation(true);
    trackEvent("Marketing Opt-In: Subscribe Clicked");
    await updateMarketingPreferences("YES", true);
  };

  const ConfirmationMessage = () => {
    return (
      <>
        <Heading size="lg" className={styles.confirmationHeader}>
          Thanks for subscribing!
        </Heading>
        <Heading size="sm" className={styles.confirmationMessage}>
          Hang tight while we take you to your SageSure account. Be sure to
          check your email inbox for your complimentary seasonal home
          maintenance checklist!
        </Heading>
        <p style={{ textAlign: "center" }}>
          <Spinner />
        </p>
        <p className={styles.disclaimer}>
          If you aren’t redirected in a few seconds,{" "}
          <DSLink
            href={finalDestination}
            data-testid="marketing-opt-in-redirect-link"
          >
            click here
          </DSLink>
          .
        </p>
      </>
    );
  };

  return (
    <BaseLayout hideFooter>
      <Container className={styles.marketingOptInContainer}>
        <Stack>
          <Card className={styles.sectionWrapperDS}>
            <Stack gap="2xl" className={styles.introContent}>
              {showConfirmation ? (
                <ConfirmationMessage />
              ) : (
                <>
                  <Heading size={isMobile ? "md" : "xl"}>
                    Join our mailing list for everything you need to protect
                    your home
                  </Heading>
                  <Heading size="xs">Here's what you'll get</Heading>
                  <Stack gap="lg">
                    <div className={styles.numberedList}>
                      <List type="number">
                        <List.Item>
                          <div className={styles.numberedListItem}>
                            A free home maintenance checklist
                          </div>
                        </List.Item>
                        <List.Item>
                          <div className={styles.numberedListItem}>
                            Exclusive SageSure offers and announcements
                          </div>
                        </List.Item>
                        <List.Item>
                          <div className={styles.numberedListItem}>
                            Expert property protection tips
                          </div>
                        </List.Item>
                      </List>
                    </div>
                  </Stack>
                  {!isMobile && (
                    <>
                      <Button
                        appearance="primary"
                        size="default"
                        className={styles.signupButton}
                        fluid
                        onPress={() => signUp()}
                        data-testid="marketing-opt-in-subscribe-btn"
                      >
                        Sign Up
                      </Button>
                      <Disclaimer />
                    </>
                  )}
                </>
              )}
            </Stack>
            <div className={styles.imageWrapper}>
              <img
                src="/assets/images/landing-page-family.jpg"
                className={styles.image}
              />
            </div>
          </Card>
          {isMobile && (
            <Stack gap="md" className={styles.mobileFooter}>
              <Button
                onPress={() => signUp()}
                fluid
                appearance="primary"
                size="sm"
              >
                Sign up
              </Button>
              {<Disclaimer />}
            </Stack>
          )}
        </Stack>
      </Container>
    </BaseLayout>
  );
};
